import { useEffect } from "react";
/**
 * Custom hook to ensure Open Graph and meta tags are positioned at the top of the <head>
 * This acts as a fallback to guarantee proper tag ordering for social media crawlers
 */
export const useMetaTagPriority = () => {
    useEffect(() => {
        let timeoutId;
        const reorderMetaTags = () => {
            const head = document.head;
            if (!head)
                return;
            // Get all meta tags and title
            const metaTags = Array.from(head.querySelectorAll('meta[property^="og:"], meta[name^="twitter:"], meta[name="description"], title'));
            // If we have meta tags, move them to the top
            if (metaTags.length > 0) {
                // Get the first child that isn't a meta tag we want to prioritize
                let insertBefore = head.firstChild;
                // Find the first style or link tag (Material UI styles)
                const firstStyle = head.querySelector('style, link[rel="stylesheet"]');
                if (firstStyle) {
                    insertBefore = firstStyle;
                }
                // Move each meta tag to before the first style tag
                metaTags.forEach((tag) => {
                    if (tag.parentNode === head && tag !== insertBefore) {
                        head.insertBefore(tag, insertBefore);
                    }
                });
                console.log(`Reordered ${metaTags.length} meta tags to top of head`);
            }
        };
        // Run reordering after a short delay to ensure all tags are rendered
        timeoutId = setTimeout(reorderMetaTags, 100);
        // Also run on any head mutations (in case tags are added dynamically)
        const observer = new MutationObserver((mutations) => {
            const hasNewTags = mutations.some((mutation) => {
                return Array.from(mutation.addedNodes).some((node) => {
                    var _a, _b;
                    if (node.nodeType === Node.ELEMENT_NODE) {
                        const element = node;
                        return (element.tagName === "STYLE" ||
                            element.tagName === "LINK" ||
                            (element.tagName === "META" &&
                                (((_a = element.getAttribute("property")) === null || _a === void 0 ? void 0 : _a.startsWith("og:")) ||
                                    ((_b = element.getAttribute("name")) === null || _b === void 0 ? void 0 : _b.startsWith("twitter:")))));
                    }
                    return false;
                });
            });
            if (hasNewTags) {
                // Debounce the reordering
                clearTimeout(timeoutId);
                timeoutId = setTimeout(reorderMetaTags, 50);
            }
        });
        if (document.head) {
            observer.observe(document.head, {
                childList: true,
                subtree: false,
            });
        }
        return () => {
            clearTimeout(timeoutId);
            observer.disconnect();
        };
    }, []);
};
